/**
 * 224 — Animation Engine
 * Senior Shopify Developer Edition — v1.0.0
 *
 * Modules:
 *  1. ScrollReveal     — IntersectionObserver for [data-anim] + [data-stagger]
 *  2. ParallaxHero     — rAF-based hero background parallax
 *  3. MagneticButtons  — magnetic hover pull on primary CTAs
 *  4. ScrollProgress   — thin red progress bar across top of page
 *  5. HeaderShrink     — adds .is-scrolled to header on scroll
 *  6. PageTransitions  — red overlay between page navigations
 *  7. CartIconPulse    — cart bubble bounce on add-to-cart events
 */

'use strict';

/* ── Utilities ─────────────────────────────────────────────── */

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const raf = (fn) => {
  if (prefersReducedMotion()) return;
  requestAnimationFrame(fn);
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

/* ── 1. SCROLL REVEAL ──────────────────────────────────────── */

const ScrollReveal = (() => {
  const VISIBLE_CLASS = 'anim-is-visible';

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = el.dataset.animDelay || '0';
        const duration = el.dataset.animDuration;

        el.style.setProperty('--anim-delay', `${delay}ms`);
        if (duration) el.style.setProperty('--anim-duration', `${duration}ms`);

        // Small rAF to ensure CSS variable is applied before class
        requestAnimationFrame(() => {
          el.classList.add(VISIBLE_CLASS);
        });

        observer.unobserve(el);
      });
    },
    {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px',
    }
  );

  const init = () => {
    if (prefersReducedMotion()) {
      // Instantly show everything
      document.querySelectorAll('[data-anim], [data-stagger]').forEach((el) => {
        el.classList.add(VISIBLE_CLASS);
      });
      return;
    }

    document.querySelectorAll('[data-anim]').forEach((el) => observer.observe(el));
    document.querySelectorAll('[data-stagger]').forEach((el) => observer.observe(el));
  };

  // Re-init after Shopify section render / AJAX
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('page:loaded', init); // view-transitions compat

  return { init };
})();


/* ── 2. PARALLAX HERO ──────────────────────────────────────── */

const ParallaxHero = (() => {
  let ticking = false;

  const update = () => {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    const bg = hero.querySelector('.hero__bg img, .hero__bg video');
    if (!bg) return;

    const scrollY = window.scrollY;
    const heroH = hero.offsetHeight;
    const isCompact = window.matchMedia('(max-width: 989px)').matches;
    const scale = isCompact ? 1.03 : 1.06;
    const maxShift = isCompact ? 28 : 60;

    // Clamp so we never leave a stale transform once scrolled past the hero.
    const pct = clamp(scrollY / heroH, 0, 1); // 0 → 1 as user scrolls through hero
    const shift = pct * maxShift;

    bg.style.transform = `translateY(${shift}px) scale(${scale})`;
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    raf(update);
  };

  const init = () => {
    if (prefersReducedMotion()) return;
    if (!document.querySelector('.hero')) return;
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  };

  return { init };
})();


/* ── 3. MAGNETIC BUTTONS ───────────────────────────────────── */

const MagneticButtons = (() => {
  const STRENGTH = 0.35; // 0 = none, 1 = full offset

  const attach = (btn) => {
    btn.classList.add('btn-magnetic');

    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) * STRENGTH;
      const dy = (e.clientY - cy) * STRENGTH;
      btn.style.setProperty('--mx', dx.toFixed(2));
      btn.style.setProperty('--my', dy.toFixed(2));
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.setProperty('--mx', '0');
      btn.style.setProperty('--my', '0');
    });
  };

  const init = () => {
    if (prefersReducedMotion()) return;
    document
      .querySelectorAll('.btn--primary, .button--primary, [name="add"]')
      .forEach(attach);
  };

  document.addEventListener('shopify:section:load', init);

  return { init };
})();


/* ── 4. SCROLL PROGRESS BAR ────────────────────────────────── */

const ScrollProgress = (() => {
  let bar = null;
  let ticking = false;

  const update = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    if (bar) bar.style.width = `${pct}%`;
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    raf(update);
  };

  const init = () => {
    if (prefersReducedMotion()) return;
    bar = document.createElement('div');
    bar.className = 'scroll-progress-224';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  };

  return { init };
})();


/* ── 5. HEADER SHRINK ──────────────────────────────────────── */

const HeaderShrink = (() => {
  const THRESHOLD = 60;
  let ticking = false;

  const update = () => {
    const header =
      document.querySelector('header-component') ||
      document.querySelector('.header-section');
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > THRESHOLD);
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      update();
    });
  };

  const init = () => {
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  };

  return { init };
})();


/* ── 6. PAGE TRANSITIONS ───────────────────────────────────── */

const PageTransitions = (() => {
  const init = () => {
    if (prefersReducedMotion()) return;

    const overlay = document.createElement('div');
    overlay.className = 'page-transition-overlay-224';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(overlay);

    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('javascript') ||
        anchor.target === '_blank' ||
        e.ctrlKey || e.metaKey || e.shiftKey
      ) return;
      e.preventDefault();
      overlay.classList.add('is-leaving');
      setTimeout(() => { window.location.href = href; }, 300);
    });

    window.addEventListener('pageshow', () => {
      overlay.classList.remove('is-leaving');
    });
  };

  return { init };
})();


/* ── 7. CART ICON PULSE ────────────────────────────────────── */

const CartIconPulse = (() => {
  const init = () => {
    document.addEventListener('cart:item-added', () => {
      const bubble = document.querySelector('.cart-bubble, [data-cart-bubble]');
      if (!bubble) return;
      bubble.classList.remove('cart-pulse-224');
      void bubble.offsetWidth;
      bubble.classList.add('cart-pulse-224');
    });
  };

  return { init };
})();


const init224Animations = () => {
  ScrollReveal.init();
  ParallaxHero.init();
  MagneticButtons.init();
  ScrollProgress.init();
  HeaderShrink.init();
  PageTransitions.init();
  CartIconPulse.init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init224Animations);
} else {
  init224Animations();
}

// Shopify theme editor re-init on section changes
document.addEventListener('shopify:section:load', () => {
  ScrollReveal.init();
  MagneticButtons.init();
});
